import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LinearNodes } from '../../../Helper/linearToTree';
import {
  TblMasterMCQ0Set,
  TblMasterMCQ2Paper,
  TblMasterMCQ3Section,
} from '../../../Interfaces/DataBase/MCQ';
import TblMasterMCQPassage from '../../../Interfaces/DataBase/MCQ2/TblMasterMCQPassage';
import TblMasterMCQQuestion from '../../../Interfaces/DataBase/MCQ2/TblMasterMCQQuestion';

interface ChapterNodes extends LinearNodes {
  MasterParentId?: number;
  MasterParentName?: string;
}

interface initialExamSetProps {
  FirstFormData: TblMasterMCQ0Set;
  MCQPaperList: TblMasterMCQ2Paper[];
  SelectedMCQPaper: TblMasterMCQ2Paper | null;
  MCQSectionList: TblMasterMCQ3Section[];
  SelectedMCQSection: TblMasterMCQ3Section | null;
  QuestionList: TblMasterMCQQuestion[];
  SelectedMCQQuestion: TblMasterMCQQuestion | null;
  MCQQuestionUnsavedFlag: boolean; // used to show unsaved question warning
  LastUsePassage?: TblMasterMCQPassage;
  MCQPassageType: 'Quill' | 'RawHTML';
  AllChaptersRaw: ChapterNodes[];
  SelectedChapterIdToFilter?: number;
}

export const initialMcqSet: initialExamSetProps = {
  FirstFormData: {
    PackageId: 0,
    MCQSetName: '',
    MCQSetNegativeMark: true,
    MCQQuestionRandom: true,
    IsFree: false,
    MCQDuration: '',
    MCQFromDate: '',
    MCQUptoDate: '',
    IsLive: false,
    LiveFromTime: '',
  },
  MCQPaperList: [],
  MCQSectionList: [],
  QuestionList: [],
  SelectedMCQQuestion: null,
  SelectedMCQSection: null,
  SelectedMCQPaper: null,
  MCQQuestionUnsavedFlag: false,
  LastUsePassage: undefined,
  MCQPassageType: 'Quill',
  AllChaptersRaw: [],
};

const MCQSetSlice = createSlice({
  name: 'McqSet',
  initialState: initialMcqSet,
  reducers: {
    setMCQFirstForm: (state, action: PayloadAction<TblMasterMCQ0Set>) => {
      state.FirstFormData = action.payload;
    },
    setMcqPaperList: (state, action: PayloadAction<TblMasterMCQ2Paper[]>) => {
      state.MCQPaperList = action.payload;
    },
    setMcqSectionList: (
      state,
      action: PayloadAction<TblMasterMCQ3Section[]>
    ) => {
      state.MCQSectionList = action.payload;
    },
    setMCQQuestionList: (
      state,
      action: PayloadAction<TblMasterMCQQuestion[]>
    ) => {
      state.QuestionList = [...action.payload];
    },
    setSelectedMCQQuestion: (
      state,
      action: PayloadAction<TblMasterMCQQuestion | null>
    ) => {
      if (!!action.payload) {
        const { PassageDetails } = action.payload;
        // console.log(PassageDetails)
        if (
          PassageDetails &&
          PassageDetails.startsWith('<section>') &&
          PassageDetails.endsWith('</section>')
        ) {
          state.MCQPassageType = 'RawHTML';
        } else {
          state.MCQPassageType = 'Quill';
        }
      }
      state.SelectedMCQQuestion = action.payload;
    },
    setSelectedMCQPaper: (
      state,
      action: PayloadAction<TblMasterMCQ2Paper | null>
    ) => {
      state.SelectedMCQPaper = action.payload;
    },
    setSelectedMCQSection: (
      state,
      action: PayloadAction<TblMasterMCQ3Section | null>
    ) => {
      state.SelectedMCQSection = action.payload;
    },
    setMCQQuestionUnsavedStatus: (state, action: PayloadAction<boolean>) => {
      state.MCQQuestionUnsavedFlag = action.payload;
    },
    setLastUseMCQPassage: (
      state,
      action: PayloadAction<TblMasterMCQPassage>
    ) => {
      state.LastUsePassage = action.payload;
    },
    setMCQPassageType: (state, action: PayloadAction<'Quill' | 'RawHTML'>) => {
      state.MCQPassageType = action.payload;
    },
    setAllChaptersRaw: (state, action: PayloadAction<LinearNodes[]>) => {
      state.AllChaptersRaw = action.payload;
    },
    setMCQChapterFilter: (state, action: PayloadAction<number | undefined>) => {
      state.SelectedChapterIdToFilter = action.payload;
    },
    setClearMcqSet: (state) => {
      state.FirstFormData = {
        PackageId: 0,
        MCQSetName: '',
        MCQSetNegativeMark: true,
        MCQQuestionRandom: true,
        IsFree: false,
        MCQDuration: '',
        MCQFromDate: '',
        MCQUptoDate: '',
        IsLive: false,
        LiveFromTime: '',
      };
      state.MCQPaperList = [];
      state.MCQSectionList = [];
      state.SelectedMCQPaper = null;
      state.MCQQuestionUnsavedFlag = false;
      state.LastUsePassage = undefined;
      state.MCQPassageType = 'Quill';
      state.AllChaptersRaw = [];
    },
  },
});

export const {
  setMCQFirstForm,
  setMCQQuestionList,
  setSelectedMCQQuestion,
  setMcqPaperList,
  setMcqSectionList,
  setClearMcqSet,
  setSelectedMCQSection,
  setSelectedMCQPaper,
  setMCQQuestionUnsavedStatus,
  setLastUseMCQPassage,
  setMCQPassageType,
  setAllChaptersRaw,
  setMCQChapterFilter,
} = MCQSetSlice.actions;

export default MCQSetSlice.reducer;
